/**
 * API service - unified config-backed endpoints
 */

import { buildEventsApiUrl, buildLocationsApiUrl } from '../config/api';

export interface Location {
  city: string;
  state: string;
  lat: number;
  lng: number;
}

export interface Event {
  id: string;
  eventID?: string;
  eventName: string;
  description?: string;
  category: string;
  listingCityName: string;
  listingStateName?: string;
  eventDateTime: string;
  itemCount?: number;
  itemNames?: string[];
  searchItemText?: string;
  distance?: number | null;
  lat?: number;
  lng?: number;
  participatingUsers?: string[];
  ownerID?: string;
  status?: string;
}

export interface Item {
  id?: string;
  name?: string;
  description?: string;
  category: string;
  condition?: string;
  userId?: string;
  userID?: string;
  firstName?: string;
  lastName?: string;
  userName?: string;
  email?: string;
  images?: string[];
  createdAt?: string;
  createdByUserId?: string;
}

export interface ItemsResponse {
  success: boolean;
  items: Item[];
  eventCategory?: string;
  participatingUserCount?: number;
  message?: string;
  error?: string;
}

export interface EventsResponse {
  success: boolean;
  events: Event[];
  totalEvents: number;
  page: number;
  totalPages: number;
  error?: string;
}

export interface StateData {
  id: number;
  name: string;
}

export async function fetchLocations(): Promise<StateData[]> {
  try {
    const response = await fetch(buildLocationsApiUrl('/api/states'));
    if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
    const data = await response.json();
    return data || [];
  } catch (error) {
    console.error('❌ Error fetching locations:', error);
    return [];
  }
}

export async function fetchCitiesForState(stateId: string): Promise<{ city: string; stateId: number; lat: number; lng: number; }[]> {
  try {
    const url = buildLocationsApiUrl(`/api/states/${stateId}/cities`);
    console.log(`📡 Fetching cities from: ${url}`);
    const response = await fetch(url);
    if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
    const data = await response.json();
    console.log(`✅ Received ${data?.length || 0} cities from API`);
    return data || [];
  } catch (error) {
    console.error('❌ Error fetching cities:', error);
    return [];
  }
}

export async function searchCities(query: string): Promise<{ city: string; state: string; stateAbbr: string; lat: number; lng: number; }[]> {
  try {
    if (!query || query.trim().length === 0) return [];
    const url = buildLocationsApiUrl(`/api/cities/search?q=${encodeURIComponent(query)}`);
    console.log(`🔍 Searching cities: ${url}`);
    const response = await fetch(url);
    if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
    const data = await response.json();
    console.log(`✅ Found ${data?.length || 0} cities matching "${query}"`);
    return data || [];
  } catch (error) {
    console.error('❌ Error searching cities:', error);
    return [];
  }
}

export async function fetchEventsWithLocation(
  location: Location | null = null,
  radius: number = 25,
  category?: string,
  timeFilter: 'future' | 'past' | 'all' = 'future',
  pagination: { page?: number; limit?: number } = {}
): Promise<EventsResponse> {
  try {
    const params = new URLSearchParams();
    if (timeFilter !== 'all') {
      params.append('timeFilter', timeFilter);
    }

    if (pagination.page) params.append('page', pagination.page.toString());
    if (pagination.limit) params.append('limit', pagination.limit.toString());
    if (category && category !== 'all') params.append('category', category);
    if (location) {
      params.append('city', location.city);
      params.append('state', location.state);
      params.append('radius', radius.toString());
    }

    params.append('_ts', Date.now().toString());

    const url = buildEventsApiUrl(`/api/events?${params.toString()}`);
    console.log(`🌐 Unified API URL: ${url}`);
    const response = await fetch(url, { cache: 'no-store' });
    if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);

    const data = await response.json();
    console.log(`✅ Loaded ${data.events?.length || 0} events from unified API`);

    return {
      success: true,
      events: data.events || [],
      totalEvents: data.totalEvents || 0,
      page: data.page || 1,
      totalPages: data.totalPages || 1
    };
  } catch (error: any) {
    console.error('❌ Error loading events:', error);
    return {
      success: false,
      events: [],
      totalEvents: 0,
      page: 1,
      totalPages: 1,
      error: error.message
    };
  }
}

export async function fetchCategories(): Promise<string[]> {
  try {
    const response = await fetch(buildEventsApiUrl('/api/categories'));
    if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
    const data = await response.json();
    return Array.isArray(data) ? data : (data.categories || []);
  } catch (error) {
    console.error('❌ Error fetching categories:', error);
    return [];
  }
}

export async function fetchItemsForEvent(eventId: string): Promise<ItemsResponse> {
  try {
    console.log(`📦 Fetching items for event: ${eventId}`);
    const response = await fetch(buildEventsApiUrl(`/api/events/${eventId}/items`));
    if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
    const data = await response.json();
    console.log(`✅ Loaded ${data.items?.length || 0} items for event`);

    return {
      success: true,
      items: data.items || [],
      eventCategory: data.eventCategory,
      participatingUserCount: data.participatingUserCount,
      message: data.message
    };
  } catch (error: any) {
    console.error('❌ Error fetching items:', error);
    return {
      success: false,
      items: [],
      error: error.message
    };
  }
}

export async function joinEventInDatabase(eventId: string, userId: string): Promise<any> {
  try {
    const response = await fetch(buildEventsApiUrl(`/api/events/${eventId}/join`), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ userId })
    });
    return await response.json();
  } catch (error) {
    console.error('❌ Error joining event:', error);
    return { success: false };
  }
}

export async function leaveEventInDatabase(eventId: string, userId: string): Promise<any> {
  try {
    const response = await fetch(buildEventsApiUrl(`/api/events/${eventId}/leave`), {
      method: 'DELETE',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ userId })
    });
    return await response.json();
  } catch (error) {
    console.error('❌ Error leaving event:', error);
    return { success: false };
  }
}

export async function getUserJoinedEventsFromDatabase(userId: string): Promise<any> {
  try {
    const response = await fetch(buildEventsApiUrl(`/api/users/${userId}/joined-events`));
    const data = await response.json();
    return {
      success: data.success || false,
      joinedEventIds: (data.joinedEventIds || data.events || []).map((event: any) => event.id).filter(Boolean)
    };
  } catch (error) {
    console.error('❌ Error getting joined events:', error);
    return {
      success: false,
      joinedEventIds: []
    };
  }
}

const apiService = {
  fetchLocations,
  fetchCitiesForState,
  searchCities,
  fetchEventsWithLocation,
  fetchCategories,
  fetchItemsForEvent,
  joinEventInDatabase,
  leaveEventInDatabase,
  getUserJoinedEventsFromDatabase
};

export default apiService;




